import { merge } from 'lodash';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { getInitialAllocatedMinutes } from 'api/resource-management';
import {
  ResourceIdentifier,
  ResourceType,
  TeamLevelAggregation,
  UserLevelAggregation,
} from 'api/resource-management/types';
import { getAllocationAggregationKey } from 'queries/resource-management';
import {
  AggregationLookup,
  AllocatedMinutes,
  DetailedAllocationData,
} from '../types';
import {
  getAllocatedMinutesFromAggregations,
  getDetailedAllocationData,
} from '../utils/common';
import {
  getKeyForProjectAggregation,
  getKeyForResourceAggregation,
} from '../utils/id';

interface Result {
  setAggregationForResourceInProject: (
    projectId: number,
    resourceIdentifier: ResourceIdentifier,
    allocatedMinutes: AllocatedMinutes,
  ) => AggregationLookup;
  removeAggregationForResource: (
    resourceIdentifier: ResourceIdentifier,
    projectId?: number,
  ) => AggregationLookup;
  getAggregationForResourceInProject: (
    projectId: number,
    resourceIdentifier: ResourceIdentifier,
  ) => UserLevelAggregation | TeamLevelAggregation | undefined;
  getAllocatedMinutesFromAggregation: (
    projectId: number,
    resourceIdentifier?: ResourceIdentifier,
  ) => DetailedAllocationData;
}
export const useAggregationManager = (): Result => {
  const queryClient = useQueryClient();

  const removeAggregationForResource = useCallback(
    (resourceIdentifier: ResourceIdentifier, projectId?: number) =>
      queryClient.setQueryData<AggregationLookup>(
        getAllocationAggregationKey(),

        (prev) => {
          const aggregationLookup = prev ?? {};
          const key = getKeyForResourceAggregation(resourceIdentifier);

          if (key != null && aggregationLookup[key] != null) {
            if (projectId != null) {
              const aggregationKey = getKeyForProjectAggregation(
                projectId,
                resourceIdentifier,
              );

              // TODO: To check if deleting the aggregation is causing any issue
              if (
                aggregationKey != null &&
                aggregationLookup[key][aggregationKey]
              ) {
                delete aggregationLookup[key][aggregationKey];
              }
            } else {
              delete aggregationLookup[key];
            }
          }

          return aggregationLookup;
        },
      ),
    [queryClient],
  );

  // TODO: To be removed from project-create socket handler
  // const getAggregationForProject = useCallback(
  //   (projectId: number, resourceIdentifiers: ResourceIdentifier[]): number => {
  //     const aggregationLookup =
  //       queryClient.getQueryData<AggregationLookup>(
  //         getAllocationAggregationKey(),
  //       ) ?? {};

  //     return resourceIdentifiers.reduce((total, resourceIdentifier) => {
  //       const key = getKeyForResourceAggregation(resourceIdentifier);
  //       const aggregationKey = getKeyForProjectAggregation(
  //         projectId,
  //         resourceIdentifier,
  //       );

  //       return (
  //         (key != null && aggregationKey != null
  //           ? aggregationLookup[key]?.[aggregationKey]?.totalMinutes ?? 0
  //           : 0) + total
  //       );
  //     }, 0);
  //   },
  //   [queryClient],
  // );

  /**
   * TODO: To be removed from project-create socket handler
   * Coz setting aggregation should be handled in allocation-state-manager
   */
  const setAggregationForResourceInProject = useCallback(
    (
      projectId: number,
      resourceIdentifier: ResourceIdentifier,
      allocatedMintutes: AllocatedMinutes = getInitialAllocatedMinutes(),
    ): AggregationLookup => {
      const aggregationLookup =
        queryClient.getQueryData<AggregationLookup>(
          getAllocationAggregationKey(),
        ) ?? {};
      const key = getKeyForResourceAggregation(resourceIdentifier);
      const aggregationKey = getKeyForProjectAggregation(
        projectId,
        resourceIdentifier,
      );

      if (key == null || aggregationKey == null) {
        return aggregationLookup;
      }
      const existingAggregation = aggregationLookup[key]?.[aggregationKey];

      const updatedAggregation = merge(
        { ...aggregationLookup },
        {
          [key]: {
            [aggregationKey]: {
              ...existingAggregation,
              ...(existingAggregation == null
                ? {
                    projectId,
                    ...resourceIdentifier,
                  }
                : {}),
              allocatedMintutes,
            },
          },
        },
      );

      return queryClient.setQueryData<AggregationLookup>(
        getAllocationAggregationKey(),
        updatedAggregation,
      );
    },
    [queryClient],
  );
  const getAggregationForResourceInProject = useCallback(
    (
      projectId: number,
      resourceIdentifier: ResourceIdentifier,
    ): UserLevelAggregation | TeamLevelAggregation | undefined => {
      const aggregationLookup =
        queryClient.getQueryData<AggregationLookup>(
          getAllocationAggregationKey(),
        ) ?? {};
      const key = getKeyForResourceAggregation(resourceIdentifier);
      const aggregationKey = getKeyForProjectAggregation(
        projectId,
        resourceIdentifier,
      );

      return aggregationLookup[key]?.[aggregationKey];
    },
    [queryClient],
  );

  const getAllocatedMinutesFromAggregation = useCallback(
    (
      projectId: number,
      resourceIdentifier?: ResourceIdentifier,
    ): DetailedAllocationData => {
      if (resourceIdentifier == null) {
        /**
         * resourceIdentifier is not present for project level action
         * so iterating over all projects and aggregating allocatedMinutes
         */

        const aggregationLookup =
          queryClient.getQueryData<AggregationLookup>(
            getAllocationAggregationKey(),
          ) ?? {};

        const projectAggregations = Object.keys(aggregationLookup).reduce(
          (result, resourceKey) => [
            ...result,
            ...Object.keys(aggregationLookup[resourceKey]).reduce(
              (filteredAggregations, aggregationKey) => {
                if (aggregationKey.includes(`project-${projectId}`)) {
                  filteredAggregations.push(
                    aggregationLookup[resourceKey][aggregationKey],
                  );
                }

                return filteredAggregations;
              },
              [] as (UserLevelAggregation | TeamLevelAggregation)[],
            ),
          ],
          [] as (UserLevelAggregation | TeamLevelAggregation)[],
        );

        return getDetailedAllocationData(
          getAllocatedMinutesFromAggregations(projectAggregations),
        );
      }

      return getDetailedAllocationData(
        getAggregationForResourceInProject(
          projectId,
          resourceIdentifier.type === ResourceType.Role
            ? {
                type: ResourceType.Team,
                teamId: resourceIdentifier.teamId,
              }
            : resourceIdentifier,
        )?.allocatedMintutes ?? getInitialAllocatedMinutes(),
      );
    },
    [getAggregationForResourceInProject, queryClient],
  );

  return {
    removeAggregationForResource,
    getAggregationForResourceInProject,
    getAllocatedMinutesFromAggregation,
    setAggregationForResourceInProject,
  };
};
